/* eslint-disable @typescript-eslint/ban-ts-comment */

import { Radio, Props as StoreblocksProps } from "@storeblocks/radio";
import { useField } from "formik";

import React, { FocusEvent, useRef } from "react";
import { isDefined, isNull } from "Utils/functions";

import Box, { Props as BoxProps } from "@storeblocks/box";
import { useButtonTexts } from "Store/texts/hooks";
import { radioTrackId } from "Utils/analytics";
import { RadioOptionProps } from "./radioTypes";
import {
  mapNumericOptionToStringOption,
  mapStringOptionToNumericOption,
} from "./radioUtils";

type Props = Omit<StoreblocksProps, "options" | "value" | "onChange"> &
  Pick<BoxProps, "marginBottom"> &
  RadioOptionProps & {
    direction?: "horizontal" | "vertical";
    onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  };

/**
 * Renders radio button group based on @storeblocks/radio.
 * Current issues:
 * TODO: @storeblocks/radio renders an unordered list (<ul><li>...</li>...</ul>) with no actual inputs (<input/>).
 */
export const RadioButton = ({
  direction,
  error,
  id,
  label,
  name,
  options,
  hint,
  onBlur,
  numeric,
  onChange,
  labelPopoverProps,
  marginBottom = "32px",
}: Props): React.ReactElement => {
  const [field, _, helpers] = useField(name);
  const ref = useRef<HTMLDivElement | null>(null);
  const [{ readMore, readLess }, lang] = useButtonTexts();

  const checkFocusState = (event: React.FocusEvent<HTMLInputElement>): void => {
    if (
      isNull(ref.current) ||
      (isDefined(ref.current) && !ref.current.contains(event.relatedTarget))
    ) {
      onBlur?.call(undefined, event);
    }
  };

  return (
    <Box ref={ref} id={id} marginBottom={marginBottom} background="inherit">
      <Radio
        {...field}
        id={id}
        value={String(field.value ?? "")}
        onChange={async (event, option) => {
          if (numeric) {
            const numericOption = mapStringOptionToNumericOption(option);
            await helpers.setValue(numericOption.value);
            await helpers.setTouched(true);
            onChange?.(numericOption);
          } else {
            await helpers.setValue(event.target.value);
            await helpers.setTouched(true);
            onChange?.(event, option);
          }
        }}
        label={label}
        options={(numeric
          ? options.map(mapNumericOptionToStringOption)
          : options
        ).map((option) => ({
          ...option,
          labelProps: {
            tabIndex: -1,
          },
          inputProps: {
            onBlur: (event) => {
              field.onBlur(event);
              checkFocusState(event);
            },
          },
          trackid: isDefined(option.trackid)
            ? option.trackid
            : radioTrackId(id),
        }))}
        direction={direction}
        error={error}
        hint={hint}
        hintProps={{
          maxLines: 6,
          readLessLabel: `${readLess[lang]}`,
          readMoreLabel: `${readMore[lang]}`,
        }}
        labelPopoverProps={labelPopoverProps}
      />
    </Box>
  );
};
